/* PLUMP CREATOR — customer-facing pillow visualiser.
 *
 * Engine (shaders, panel profile, mesh handling, camera) is carried over from the
 * supplied prototype. What changed:
 *   - no DOM control lookups: every uniform is read from this.settings, so the
 *     component can be driven by props from anywhere.
 *   - one authoritative artwork state (this.settings.image + this._tex). Setting a
 *     new image deletes the previous GL texture, so a stale/demo print can never
 *     come back.
 *   - render loop pauses when off-screen or the tab is hidden, and stops on
 *     disconnect; all GL resources are released.
 *   - developer tuning controls only exist when DEBUG_VISUALISER is true.
 *
 * Public API (also settable as attributes):
 *   el.setImage(urlOrNull)      authoritative customer artwork
 *   el.setPlumpness(0|1|2)      Soft / Plump / Extra plump
 *   el.setSize(200|300|400)     product size in mm
 *   el.resetView()
 */
(function () {
  const DEBUG_VISUALISER = false;

  const MESH_URL = 'assets/pillow-mesh.js';
  const BG_URL = 'plumplo-bgremove.js';

  let bgPromise = null;
  function loadBG() {
    if (window.PlumploBG) return Promise.resolve(window.PlumploBG);
    if (bgPromise) return bgPromise;
    bgPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = BG_URL;
      s.onload = () => resolve(window.PlumploBG);
      s.onerror = () => reject(new Error('background remover failed to load'));
      document.head.appendChild(s);
    });
    return bgPromise;
  }

  /* The one authoritative default view. Rotation lives on the mesh group (yaw/pitch),
     distance lives on the camera; resetView restores all three and nothing else. */
  const DEFAULT_VIEW = { yaw: 0, pitch: -0.05, dist: 900 };
  const DIST_MIN = 620, DIST_MAX = 1500;
  const PITCH_LIMIT = 0.55;
  const RESET_MS = 420;
  const AUTOROTATE = 0.30;        /* radians per second when idle */
  const RESUME_MS = 900;          /* idle time after an interaction before it drifts again */
  const TAU = Math.PI * 2;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  /* Ladder shifted down one step: Extra Plumpy takes the old Plumpy, Plumpy takes the
     old Soft, and Soft is flatter again. Depth/inflation only — silhouette, artwork,
     bleed and seam are untouched. */
  const PLUMP_STEPS = [
    { tubby: 0.26, puff: 0.66, depth: 64 },
    { tubby: 0.36, puff: 0.69, depth: 71 },
    { tubby: 0.45, puff: 0.72, depth: 78 }
  ];

  let meshPromise = null;
  function loadMesh() {
    if (window.PLUMPY_MESH) return Promise.resolve(window.PLUMPY_MESH);
    if (meshPromise) return meshPromise;
    meshPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = MESH_URL;
      s.onload = () => resolve(window.PLUMPY_MESH);
      s.onerror = () => reject(new Error('pillow mesh failed to load'));
      document.head.appendChild(s);
    });
    return meshPromise;
  }

  const VS = `
attribute vec3 aPos;attribute vec3 aNormal;attribute vec2 aUV;attribute float aEdge;
uniform mat4 uMVP;uniform mat4 uModel;uniform float uPuff;uniform float uTubby;uniform float uDepth;uniform float uHeight;uniform float uZScale;
varying vec3 vN;varying vec2 vUV;varying float vEdge;varying vec2 vXY;
void main(){
 vec3 p=aPos;
 float side=p.z<0.0?-1.0:1.0;
 float seamT=clamp(aEdge/18.0,0.0,1.0);
 float seamRoll=pow(sin(seamT*1.57079632679),0.90);
 float domeT=clamp(aEdge/150.0,0.0,1.0);
 float broadDome=pow(sin(domeT*1.57079632679),0.48);
 float deepT=clamp(aEdge/285.0,0.0,1.0);
 float deepDome=pow(sin(deepT*1.57079632679),0.58);
 float yn=clamp(p.y/max(uHeight,1.0)+0.5,0.0,1.0);
 float head=exp(-pow((yn-0.70)/0.27,2.0));
 float middle=exp(-pow((yn-0.50)/0.38,2.0));
 float lower=exp(-pow((yn-0.30)/0.28,2.0));
 float puffN=clamp((uPuff-0.60)/0.40,0.0,1.0);
 float tub=clamp(uTubby,0.0,2.0);
 float zone=0.90 + 0.08*head + (0.08+0.16*min(tub,1.0))*middle + 0.08*lower;
 float overTub=max(tub-1.0,0.0);
 zone += overTub*(0.08*head + 0.20*middle + 0.10*lower);
 float centreCarry=mix(0.80,1.12,puffN)*mix(0.91,1.10,min(tub,1.0));
 centreCarry *= 1.0 + 0.16*overTub;
 float pressure=mix(seamRoll,broadDome,0.73);
 pressure=mix(pressure,deepDome,(0.30+0.18*min(tub,1.0)+0.12*overTub)*centreCarry);
 float barrel=exp(-pow((yn-0.50)/0.43,2.0));
 pressure*=1.0+(0.025+0.14*min(tub,1.0)+0.20*overTub)*puffN*barrel;
 pressure=clamp(pressure*centreCarry,0.0,1.0);
 float halfDepth=max(3.0,uDepth*0.5);
 /* Seam thickness tapers to zero at the rim so the two shells meet in a closed
    edge (no gap in side view), then opens quickly so the panels never sit
    coplanar across a band and z-fight. */
 p.z=side*(halfDepth*zone*pressure+0.9*seamRoll)*uZScale;
 vN=normalize(mat3(uModel)*aNormal);vUV=aUV;vEdge=aEdge;vXY=p.xy;
 gl_Position=uMVP*vec4(p,1.0);
}`;

  const FS = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform sampler2D uTex;uniform float uUseTex;uniform float uVelvet;uniform vec3 uBase;uniform float uStitchN;uniform float uWeaveVis;
varying vec3 vN;varying vec2 vUV;varying float vEdge;varying vec2 vXY;
float hash(vec2 p){vec3 q=fract(vec3(p.xyx)*.1031);q+=dot(q,q.yzx+19.19);return fract((q.x+q.y)*q.z);}
void main(){
 vec4 t=texture2D(uTex,vUV);
 vec3 tex=uUseTex>.5?mix(vec3(.985,.98,.955),t.rgb,t.a):uBase;
 vec3 N=normalize(vN),L=normalize(vec3(-.35,.45,.82));

 /* Woven micro-structure. A plain weave is warp over weft in alternating cells, so
    the surface is a grid of tiny rounded ridges rather than a flat plane. Perturbing
    the normal by that grid is what makes the light read as cloth instead of paper.
    uWeaveVis fades it out when a thread is smaller than a pixel — otherwise the weave
    aliases into a moiré screen, which is worse than no weave at all. */
 float wv=uWeaveVis*uVelvet;
 vec2 w=vUV*240.0;                                   /* ~1.2mm threads (see WEAVE_FREQ) */
 vec2 wf=fract(w)-0.5;
 float cell=mod(floor(w.x)+floor(w.y),2.0);          /* over / under */
 vec2 ridge=mix(vec2(wf.x,wf.y*0.35),vec2(wf.x*0.35,wf.y),cell);
 vec3 Nw=normalize(N+vec3(ridge.x,ridge.y,0.0)*0.10*wv);
 /* thread gaps sit slightly deeper, so they catch less light */
 float gap=1.0-0.028*wv*smoothstep(0.30,0.5,max(abs(wf.x),abs(wf.y)));

 /* Fibre nap and a slower slub variation, so the cloth is not mechanically even. */
 float nap=(hash(vUV*900.0)-.5)*.014*uVelvet;
 float slub=(hash(floor(vUV*46.0))-.5)*.014*uVelvet;

 float d=max(dot(Nw,L),0.0);
 float wrap=max(dot(Nw,L)*0.5+0.5,0.0);              /* soft wrap light — fabric scatters */
 float grazing=pow(1.0-abs(dot(N,vec3(0.0,0.0,1.0))),1.7);
 /* Broad, weak sheen. Cotton has no sharp highlight, just a slight lift. */
 vec3 H=normalize(L+vec3(0.0,0.0,1.0));
 float sheen=pow(max(dot(Nw,H),0.0),9.0)*0.035*uVelvet;

 vec3 col;
 if(uUseTex<.5){ col=uBase; }
 else {
   /* Ink soaks into cloth: printed whites never reach paper-white and saturation
      drops a touch. Without this the surface reads as a screen, not a pillow. */
   float ink=dot(tex,vec3(.299,.587,.114));
   vec3 fabric=mix(tex,vec3(ink),0.05)*0.985;
   col=fabric*(0.945+0.055*d)*(0.97+0.055*wrap)*gap
       +vec3(sheen)+vec3(grazing*.008*uVelvet)+nap+slub;
 }
 /* Narrow soft shading along the sewn rim so the seam reads as fabric rather than
    a bright stretched streak, plus a wide gentle occlusion as the cloth turns away. */
 float seam=1.0-smoothstep(0.0,7.0,vEdge);
 col*=1.0-0.20*seam;
 col*=0.955+0.045*smoothstep(0.0,34.0,vEdge);
 col=clamp(col,vec3(.04),vec3(.985));
 gl_FragColor=vec4(col,1.0);
}`;

  const NOMINAL = 300;   /* dynamic silhouettes are authored in this mm space */
  const WEAVE_FREQ = 240;  /* thread cells across the canvas — must match the shader */
  const BLEED_DEFAULT = 12;      /* mm, expressed against NOMINAL — resolution independent */
  /* The reverse message's typeface. The storefront may override it per design via the
     reverse-font attribute; anything falsy falls back to this, so designs saved before the
     font picker existed composite exactly as they did before. */
  const REVERSE_FONT_DEFAULT = '"Great Vibes", cursive';
  const CLOSE_FRAC = 0.035;      /* gap-bridging radius, fraction of canvas */
  const SMOOTH_PASSES = 3;

  /* ---- silhouette extraction ---------------------------------------------
     A pillow's outline is cut from the customer's own artwork. Alpha is trusted
     when the upload already carries a cutout; otherwise the background is flood
     filled inward from the border by colour similarity. */
  function maskFromImage(img) {
    const MAX = 340;
    const s = Math.min(MAX / img.width, MAX / img.height, 1);
    const w = Math.max(8, Math.round(img.width * s)), h = Math.max(8, Math.round(img.height * s));
    const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    const g = cv.getContext('2d', { willReadFrequently: true });
    if (!g) return null;
    g.drawImage(img, 0, 0, w, h);
    let d;
    try { d = g.getImageData(0, 0, w, h).data; } catch (e) { return null; }
    const n = w * h, mask = new Uint8Array(n);
    let clear = 0;
    for (let i = 0; i < n; i++) if (d[i * 4 + 3] < 230) clear++;

    if (clear > n * 0.02) {
      for (let i = 0; i < n; i++) mask[i] = d[i * 4 + 3] > 128 ? 1 : 0;
    } else {
      let br = 0, bg = 0, bb = 0, c = 0;
      const acc = (i) => { br += d[i * 4]; bg += d[i * 4 + 1]; bb += d[i * 4 + 2]; c++; };
      for (let x = 0; x < w; x++) { acc(x); acc((h - 1) * w + x); }
      for (let y = 0; y < h; y++) { acc(y * w); acc(y * w + w - 1); }
      br /= c; bg /= c; bb /= c;
      const TOL = 62 * 62;
      const isBg = (i) => {
        const a = d[i * 4] - br, b = d[i * 4 + 1] - bg, e = d[i * 4 + 2] - bb;
        return a * a + b * b + e * e < TOL;
      };
      const back = new Uint8Array(n), st = [];
      const seed = (i) => { if (!back[i] && isBg(i)) { back[i] = 1; st.push(i); } };
      for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
      for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
      while (st.length) {
        const i = st.pop(), x = i % w, y = (i / w) | 0;
        if (x > 0) seed(i - 1); if (x < w - 1) seed(i + 1);
        if (y > 0) seed(i - w); if (y < h - 1) seed(i + w);
      }
      for (let i = 0; i < n; i++) mask[i] = back[i] ? 0 : 1;
    }

    /* keep the largest blob and anything comparable to it (a raised tail may be
       separated by the matte); drop specks of garden and wall */
    const lab = new Int32Array(n).fill(-1); const areas = [];
    for (let i = 0; i < n; i++) {
      if (!mask[i] || lab[i] >= 0) continue;
      const id = areas.length, st2 = [i]; lab[i] = id; let a = 0;
      while (st2.length) {
        const p = st2.pop(); a++;
        const x = p % w, y = (p / w) | 0;
        const push = (q) => { if (mask[q] && lab[q] < 0) { lab[q] = id; st2.push(q); } };
        if (x > 0) push(p - 1); if (x < w - 1) push(p + 1);
        if (y > 0) push(p - w); if (y < h - 1) push(p + w);
      }
      areas.push(a);
    }
    if (!areas.length) return null;
    let best = 0; areas.forEach((a, i) => { if (a > areas[best]) best = i; });
    if (areas[best] < n * 0.01) return null;
    const floor = Math.max(areas[best] * 0.12, n * 0.004);
    for (let i = 0; i < n; i++) mask[i] = (lab[i] >= 0 && areas[lab[i]] >= floor) ? 1 : 0;

    const out = new Uint8Array(n), st3 = [];
    const solid = mask.slice();          // geometry wants the holes closed
    const seedOut = (i) => { if (!out[i] && !mask[i]) { out[i] = 1; st3.push(i); } };
    for (let x = 0; x < w; x++) { seedOut(x); seedOut((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { seedOut(y * w); seedOut(y * w + w - 1); }
    while (st3.length) {
      const i = st3.pop(), x = i % w, y = (i / w) | 0;
      if (x > 0) seedOut(i - 1); if (x < w - 1) seedOut(i + 1);
      if (y > 0) seedOut(i - w); if (y < h - 1) seedOut(i + w);
    }
    for (let i = 0; i < n; i++) if (!mask[i] && !out[i]) solid[i] = 1;
    /* mask = artwork (holes NOT filled, so background pockets take the border
       colour instead of printing grass); solid = pillow shape (holes filled). */
    return { mask, solid, w, h, data: d };
  }

  /* An image with meaningful transparency has already been cut out by something else.
     Sampled on a small canvas: cheap, and a few percent of clear pixels is decisive. */
  function alreadyCutOut(img) {
    try {
      const S = 96;
      const cv = document.createElement('canvas');
      cv.width = S; cv.height = Math.max(1, Math.round(S * img.height / img.width));
      const g = cv.getContext('2d', { willReadFrequently: true });
      if (!g) return false;
      g.drawImage(img, 0, 0, cv.width, cv.height);
      const d = g.getImageData(0, 0, cv.width, cv.height).data;
      let clear = 0;
      for (let i = 0; i < cv.width * cv.height; i++) if (d[i * 4 + 3] < 200) clear++;
      return clear > cv.width * cv.height * 0.02;
    } catch (e) { return false; }
  }

  /* chamfer distance to the nearest pixel equal to seed */
  function chamfer(mask, w, h, seed) {
    const BIG = 1e6, dist = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) dist[i] = mask[i] === seed ? 0 : BIG;
    const a = 1, b = Math.SQRT2;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x; let v = dist[i];
      if (y > 0) { v = Math.min(v, dist[i - w] + a); if (x > 0) v = Math.min(v, dist[i - w - 1] + b); if (x < w - 1) v = Math.min(v, dist[i - w + 1] + b); }
      if (x > 0) v = Math.min(v, dist[i - 1] + a);
      dist[i] = v;
    }
    for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
      const i = y * w + x; let v = dist[i];
      if (y < h - 1) { v = Math.min(v, dist[i + w] + a); if (x > 0) v = Math.min(v, dist[i + w - 1] + b); if (x < w - 1) v = Math.min(v, dist[i + w + 1] + b); }
      if (x < w - 1) v = Math.min(v, dist[i + 1] + a);
      dist[i] = v;
    }
    return dist;
  }

  /* ---- morphology on the mask grid --------------------------------------- */
  function dilate(mask, w, h, r) {
    const d = chamfer(mask, w, h, 1), o = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) o[i] = d[i] <= r ? 1 : 0;
    return o;
  }
  function erode(mask, w, h, r) {
    const d = chamfer(mask, w, h, 0), o = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) o[i] = d[i] > r ? 1 : 0;
    return o;
  }
  /* Closing bridges the gaps a stuffed pillow could not be sewn around — between
     legs, under a tail — without touching the artwork. */
  function morphClose(mask, w, h, r) { return erode(dilate(mask, w, h, r), w, h, r); }
  /* Opening strips thin appendages and hard-edged background blocks the segmentation
     leaves attached to the subject, without eating ears, legs or tails. */
  function morphOpen(mask, w, h, r) { return dilate(erode(mask, w, h, r), w, h, r); }

  function signedField(mask, w, h) {
    const din = chamfer(mask, w, h, 0), dout = chamfer(mask, w, h, 1);
    const f = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) f[i] = mask[i] ? din[i] : -dout[i];
    return f;
  }

  /* Blurring the field before contouring rounds the outline the way a sewn edge
     rounds — cheaper and stabler than smoothing a traced polygon. */
  function blurField(f, w, h, passes) {
    let src = f, tmp = new Float32Array(w * h);
    for (let p = 0; p < passes; p++) {
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const a = src[y * w + Math.max(0, x - 1)], b = src[y * w + x], c = src[y * w + Math.min(w - 1, x + 1)];
        tmp[y * w + x] = (a + b + b + c) * 0.25;
      }
      for (let x = 0; x < w; x++) for (let y = 0; y < h; y++) {
        const a = tmp[Math.max(0, y - 1) * w + x], b = tmp[y * w + x], c = tmp[Math.min(h - 1, y + 1) * w + x];
        src[y * w + x] = (a + b + b + c) * 0.25;
      }
    }
    return src;
  }

  /* Size-proportional smoothing of the pillow contour. A sewn rim cannot reproduce a
     small spike, so matte noise must not become a protrusion. Box blur x3 approximates
     a Gaussian cheaply; applied to the OUTER field only, never the artwork. */
  function boxBlurField(f, w, h, r, passes) {
    if (r < 1) return f;
    const k = 2 * r + 1;
    let src = f;
    const tmp = new Float32Array(w * h);
    const cx = (x) => Math.min(w - 1, Math.max(0, x));
    const cy = (y) => Math.min(h - 1, Math.max(0, y));
    for (let p = 0; p < passes; p++) {
      for (let y = 0; y < h; y++) {
        const row = y * w;
        let acc = 0;
        for (let x = -r; x <= r; x++) acc += src[row + cx(x)];
        for (let x = 0; x < w; x++) {
          tmp[row + x] = acc / k;
          acc += src[row + cx(x + r + 1)] - src[row + cx(x - r)];
        }
      }
      for (let x = 0; x < w; x++) {
        let acc = 0;
        for (let y = -r; y <= r; y++) acc += tmp[cy(y) * w + x];
        for (let y = 0; y < h; y++) {
          src[y * w + x] = acc / k;
          acc += tmp[cy(y + r + 1) * w + x] - tmp[cy(y - r) * w + x];
        }
      }
    }
    return src;
  }

  function bilinear(f, w, h, x, y) {
    const x0 = Math.max(0, Math.min(w - 1, Math.floor(x))), y0 = Math.max(0, Math.min(h - 1, Math.floor(y)));
    const x1 = Math.min(w - 1, x0 + 1), y1 = Math.min(h - 1, y0 + 1);
    const tx = Math.max(0, Math.min(1, x - x0)), ty = Math.max(0, Math.min(1, y - y0));
    const a = f[y0 * w + x0] + (f[y0 * w + x1] - f[y0 * w + x0]) * tx;
    const b = f[y1 * w + x0] + (f[y1 * w + x1] - f[y1 * w + x0]) * tx;
    return a + (b - a) * ty;
  }

  const hexToRgb = (hex) => {
    const m = /^#?([\da-f]{6})$/i.exec(String(hex || ''));
    if (!m) return [226, 168, 143];
    const v = parseInt(m[1], 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  };
  const rgbToHex = (c) => '#' + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

  /* CPU mirror of the vertex shader's stuffed-panel profile, at nominal plumpness.
     Used only to derive normals for a freshly cut silhouette. */
  function profileZ(edge, yn) {
    const tub = 1.0, puff = 0.86, depth = 104;
    const cl = (v) => Math.min(Math.max(v, 0), 1);
    const seamRoll = Math.pow(Math.sin(cl(edge / 18) * 1.5707963), 0.90);
    const broadDome = Math.pow(Math.sin(cl(edge / 150) * 1.5707963), 0.48);
    const deepDome = Math.pow(Math.sin(cl(edge / 285) * 1.5707963), 0.58);
    const head = Math.exp(-Math.pow((yn - 0.70) / 0.27, 2));
    const middle = Math.exp(-Math.pow((yn - 0.50) / 0.38, 2));
    const lower = Math.exp(-Math.pow((yn - 0.30) / 0.28, 2));
    const puffN = cl((puff - 0.60) / 0.40);
    const zone = 0.90 + 0.08 * head + (0.08 + 0.16 * Math.min(tub, 1)) * middle + 0.08 * lower;
    const centreCarry = (0.80 + 0.32 * puffN) * (0.91 + 0.19 * Math.min(tub, 1));
    let pressure = seamRoll + (broadDome - seamRoll) * 0.73;
    pressure += (deepDome - pressure) * (0.30 + 0.18 * Math.min(tub, 1)) * centreCarry;
    const barrel = Math.exp(-Math.pow((yn - 0.50) / 0.43, 2));
    pressure *= 1 + (0.025 + 0.14 * Math.min(tub, 1)) * puffN * barrel;
    return Math.max(3, depth * 0.5) * zone * cl(pressure * centreCarry);
  }

  const I = () => [1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1];
  function mul(a,b){const r=Array(16).fill(0);for(let c=0;c<4;c++)for(let q=0;q<4;q++)for(let k=0;k<4;k++)r[c*4+q]+=a[k*4+q]*b[c*4+k];return r;}
  function ry(a){const c=Math.cos(a),s=Math.sin(a);return[c,0,-s,0,0,1,0,0,s,0,c,0,0,0,0,1];}
  function rx(a){const c=Math.cos(a),s=Math.sin(a);return[1,0,0,0,0,c,s,0,0,-s,c,0,0,0,0,1];}
  function scM(s){return[s,0,0,0,0,s,0,0,0,0,s,0,0,0,0,1];}
  function trM(z){const m=I();m[14]=z;return m;}
  function persp(f,a,n,F){const q=1/Math.tan(f/2),nf=1/(n-F);return[q/a,0,0,0,0,q,0,0,0,0,(F+n)*nf,-1,0,0,2*F*n*nf,0];}

  class PlumpyVisualiser extends HTMLElement {
    constructor() {
      super();
      /* single authoritative creator state for this instance */
      this.settings = { image: null, plump: 1, size: 300, velvet: 1,
                        bleedMode: 'color', bleedColor: '#FFFFFF', bleedWidth: BLEED_DEFAULT,
                        reverseText: '', reverseFont: REVERSE_FONT_DEFAULT, backPrint: true };
      this._geom = null; this._img = null; this._suggest = [];
      this._view = Object.assign({}, DEFAULT_VIEW);
      this._drag = null; this._ptrs = new Map(); this._pinch = null; this._tween = null;
      this._resumeAt = 0; this._lastT = 0;
      this._meshes = {}; this._tex = null; this._texToken = 0; this._dynamic = null;
      this._running = false; this._visible = true;
    }

    /* React lands hyphenated attrs on a custom element with the hyphen stripped,
       so both spellings are observed — and properties are mirrored too, since React
       may set a property rather than an attribute. */
    static get observedAttributes() {
      return ['image', 'plumpness', 'size',
              'bleed-mode', 'bleed-color', 'bleed-width',
              'bleedmode', 'bleedcolor', 'bleedwidth',
              'reverse-text', 'reversetext',
              'reverse-font', 'reversefont',
              'back-print', 'backprint'];
    }

    attributeChangedCallback(name, _old, value) {
      if (name === 'image') this.setImage(value || null);
      else if (name === 'plumpness') this.setPlumpness(+value || 0);
      else if (name === 'size') this.setSize(+value || 300);
      else if (name === 'bleed-mode' || name === 'bleedmode') this.setBleed({ mode: value || 'auto' });
      else if (name === 'bleed-color' || name === 'bleedcolor') this.setBleed({ color: value || '#E2A88F' });
      else if (name === 'bleed-width' || name === 'bleedwidth') this.setBleed({ width: +value || BLEED_DEFAULT });
      else if (name === 'reverse-text' || name === 'reversetext') this.setReverse(value);
      else if (name === 'reverse-font' || name === 'reversefont') this.setReverseFont(value);
      else if (name === 'back-print' || name === 'backprint') this.setBackPrint(value === '1' || value === 'true');
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this.style.display = 'block';
      this.style.position = 'relative';
      this.style.width = '100%';
      this.style.height = '100%';
      this.style.touchAction = 'pan-y';

      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'display:block;width:100%;height:100%;cursor:grab;outline-offset:6px';
      canvas.tabIndex = 0;
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', 'Your pillow preview. Drag or use arrow keys to turn it, scroll to zoom, Home to reset the view.');
      this.appendChild(canvas);
      this._canvas = canvas;

      const pinchDist = () => {
        const p = [...this._ptrs.values()];
        return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      };

      canvas.addEventListener('pointerdown', (e) => {
        this._tween = null;                       // a new gesture owns the view
        this._hold();
        this._ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
        canvas.setPointerCapture(e.pointerId);
        if (this._ptrs.size === 2) {
          this._drag = null;
          this._pinch = { d0: pinchDist(), dist0: this._view.dist };
        } else {
          this._drag = { id: e.pointerId, x: e.clientX, y: e.clientY, yaw: this._view.yaw, pitch: this._view.pitch };
          this._engaged = true;
          canvas.style.cursor = 'grabbing';
        }
      });

      canvas.addEventListener('pointermove', (e) => {
        if (!this._ptrs.has(e.pointerId)) return;
        this._hold();
        this._ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this._pinch && this._ptrs.size === 2) {
          const d = pinchDist();
          if (d > 0 && this._pinch.d0 > 0) {
            this._view.dist = Math.max(DIST_MIN, Math.min(DIST_MAX, this._pinch.dist0 * (this._pinch.d0 / d)));
          }
          return;
        }
        const g = this._drag;
        if (!g || g.id !== e.pointerId) return;    // a reset mid-drag clears this
        this._view.yaw = g.yaw + (e.clientX - g.x) * 0.009;
        this._view.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, g.pitch - (e.clientY - g.y) * 0.005));
      });

      const end = (e) => {
        this._hold();
        this._ptrs.delete(e.pointerId);
        if (this._ptrs.size < 2) this._pinch = null;
        if (this._drag && this._drag.id === e.pointerId) this._drag = null;
        if (!this._ptrs.size) canvas.style.cursor = 'grab';
      };
      canvas.addEventListener('pointerup', end);
      canvas.addEventListener('pointercancel', end);

      /* Page scroll wins by default: the canvas is a wide band in the hero, so an
         unconditional preventDefault would trap a desktop scroll. Zoom needs intent —
         ctrl/meta (also what trackpad pinch sends) or a prior click/focus into it. */
      canvas.addEventListener('focus', () => { this._engaged = true; });
      canvas.addEventListener('blur', () => { this._engaged = false; });
      canvas.addEventListener('pointerenter', () => { if (document.activeElement !== canvas) this._engaged = false; });
      canvas.addEventListener('wheel', (e) => {
        if (!(e.ctrlKey || e.metaKey || this._engaged)) return;   // let the page scroll
        e.preventDefault();
        this._tween = null;
        this._hold();
        this._view.dist = Math.max(DIST_MIN, Math.min(DIST_MAX, this._view.dist * (1 + Math.sign(e.deltaY) * 0.09)));
      }, { passive: false });

      canvas.addEventListener('keydown', (e) => {
        const v = this._view;
        this._hold();
        if (e.key === 'ArrowLeft') { e.preventDefault(); this._tween = null; v.yaw -= 0.25; }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this._tween = null; v.yaw += 0.25; }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this._tween = null; v.pitch = Math.max(-PITCH_LIMIT, v.pitch - 0.12); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); this._tween = null; v.pitch = Math.min(PITCH_LIMIT, v.pitch + 0.12); }
        else if (e.key === 'Home' || e.key === '0') { e.preventDefault(); this.resetView(); }
      });

      this._io = new IntersectionObserver((entries) => {
        this._visible = entries.some((x) => x.isIntersecting);
        if (this._visible) this._start(); else this._stop();
      }, { threshold: 0.05 });
      this._io.observe(this);
      this._onVis = () => { if (document.hidden) this._stop(); else if (this._visible) this._start(); };
      document.addEventListener('visibilitychange', this._onVis);

      loadMesh().then((mesh) => {
        if (!this.isConnected) return;
        this._initGL(mesh);
        if (DEBUG_VISUALISER) this._debugPanel();
        this._start();
      }).catch((err) => {
        this._fallback(err);
      });
    }

    disconnectedCallback() {
      this._stop();
      if (this._io) this._io.disconnect();
      document.removeEventListener('visibilitychange', this._onVis);
      this._releaseGL();
    }

    /* ---- authoritative artwork state -------------------------------- */
    setImage(src) {
      this.settings.image = src || null;
      const token = ++this._texToken;         // invalidates any in-flight load
      if (!this._gl) return;
      if (!src) { this._swapTexture(null); this._geom = null; this._img = null; this._cutout = null; this._bounds = null; this._disposeDynamic(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (token !== this._texToken || !this._gl) return;   // a newer upload won
        this._process(img, token);
      };
      img.onerror = () => { if (token === this._texToken) { this._swapTexture(null); this._geom = null; this._disposeDynamic(); } };
      img.src = src;
    }

    setPlumpness(i) { this.settings.plump = Math.max(0, Math.min(2, i | 0)); }

    /* Reverse-side message. Only the back texture is rebuilt: artwork, masks and
       geometry are untouched. */
    setReverse(text) {
      const t = text == null ? '' : String(text);
      if (t === this.settings.reverseText) return;
      this.settings.reverseText = t;
      if (this._geom && this._gl) this._recomposite();
    }

    /* Reverse-side typeface. Like setReverse, only the back texture is rebuilt: artwork,
       masks and geometry are untouched. An empty or missing value restores the original
       Great Vibes default, so a design saved before fonts existed renders as it always did. */
    setReverseFont(font) {
      const f = font == null || String(font).trim() === '' ? REVERSE_FONT_DEFAULT : String(font);
      if (f === this.settings.reverseFont) return;
      this.settings.reverseFont = f;
      if (this._geom && this._gl) this._recomposite();
    }

    setBackPrint(on) {
      const v = !!on;
      if (v === this.settings.backPrint) return;
      this.settings.backPrint = v;
      if (this._geom && this._gl) this._recomposite();
    }

    /* Bleed changes never touch the artwork or the masks. A width change re-cuts the
       outer contour; a colour or mode change only re-composites the texture. */
    setBleed(patch) {
      const s = this.settings;
      const widthChanged = patch.width !== undefined && +patch.width !== s.bleedWidth;
      const modeChanged = patch.mode !== undefined && patch.mode !== s.bleedMode;
      if (patch.mode !== undefined) s.bleedMode = patch.mode;
      if (patch.color !== undefined) s.bleedColor = patch.color;
      if (patch.width !== undefined) s.bleedWidth = Math.max(0, +patch.width);
      if (!this._geom || !this._gl) return;
      /* switching to or from "none" changes how far the outline is offset */
      if (widthChanged || modeChanged) this._rebuildFromGeom();
      else this._recomposite();
    }
    setSize(mm) { this.settings.size = [200, 300, 400].indexOf(+mm) >= 0 ? +mm : 300; }
    /* Restores yaw, pitch and camera distance only. Never touches this.settings,
       so artwork, plumpness and size survive untouched. */
    resetView() {
      this._drag = null; this._pinch = null; this._ptrs.clear();
      if (this._canvas) this._canvas.style.cursor = 'grab';
      const v = this._view;
      /* unwind accumulated turns so the tween takes the short way round */
      const yaw = ((v.yaw % TAU) + TAU) % TAU;
      v.yaw = yaw > Math.PI ? yaw - TAU : yaw;
      const settled = Math.abs(v.yaw - DEFAULT_VIEW.yaw) < 0.001 &&
                      Math.abs(v.pitch - DEFAULT_VIEW.pitch) < 0.001 &&
                      Math.abs(v.dist - DEFAULT_VIEW.dist) < 0.5;
      if (settled || !this._running || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._view = Object.assign({}, DEFAULT_VIEW);
        this._tween = null;
        return;
      }
      this._tween = { from: Object.assign({}, v), t0: performance.now() };
    }

    /* Idle drift, so the pillow shows itself off without the customer having to
       discover the drag. Any interaction pauses it; it resumes a couple of seconds
       after the last one. Held still for reduced-motion users. */
    _autorotate(now) {
      const dt = this._lastT ? Math.min(0.05, (now - this._lastT) / 1000) : 0;
      this._lastT = now;
      if (this._drag || this._pinch || this._tween) return;
      if (now < this._resumeAt) return;
      if (this._reduced === undefined) {
        this._reduced = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
      }
      if (this._reduced) return;
      this._view.yaw += AUTOROTATE * dt;
    }

    _hold() { this._resumeAt = performance.now() + RESUME_MS; }

    _applyTween(now) {
      const tw = this._tween;
      if (!tw) return;
      const k = easeOutCubic(Math.min(1, (now - tw.t0) / RESET_MS));
      for (const key of ['yaw', 'pitch', 'dist']) {
        this._view[key] = tw.from[key] + (DEFAULT_VIEW[key] - tw.from[key]) * k;
      }
      if (k >= 1) { this._view = Object.assign({}, DEFAULT_VIEW); this._tween = null; }
    }

    /* ---- GL --------------------------------------------------------- */
    _initGL(mesh) {
      const gl = this._canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: false });
      if (!gl) { this._fallback(new Error('WebGL unavailable')); return; }
      this._gl = gl;
      const sh = (type, src) => {
        const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
        return s;
      };
      const prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog); gl.useProgram(prog);
      this._prog = prog;
      this._L = {
        pos: gl.getAttribLocation(prog, 'aPos'), n: gl.getAttribLocation(prog, 'aNormal'),
        uv: gl.getAttribLocation(prog, 'aUV'), edge: gl.getAttribLocation(prog, 'aEdge'),
        mvp: gl.getUniformLocation(prog, 'uMVP'), model: gl.getUniformLocation(prog, 'uModel'),
        tex: gl.getUniformLocation(prog, 'uTex'), use: gl.getUniformLocation(prog, 'uUseTex'),
        vel: gl.getUniformLocation(prog, 'uVelvet'), base: gl.getUniformLocation(prog, 'uBase'),
        zscale: gl.getUniformLocation(prog, 'uZScale'), stitchN: gl.getUniformLocation(prog, 'uStitchN'),
        weave: gl.getUniformLocation(prog, 'uWeaveVis'),
        puff: gl.getUniformLocation(prog, 'uPuff'), tubby: gl.getUniformLocation(prog, 'uTubby'),
        depth: gl.getUniformLocation(prog, 'uDepth'), height: gl.getUniformLocation(prog, 'uHeight')
      };
      gl.uniform1i(this._L.tex, 0);

      const norms = (V, F) => {
        const n = V.map(() => [0, 0, 0]);
        for (const f of F) {
          const a = V[f[0]], b = V[f[1]], c = V[f[2]];
          const u = [b[0]-a[0], b[1]-a[1], b[2]-a[2]], v = [c[0]-a[0], c[1]-a[1], c[2]-a[2]];
          const q = [u[1]*v[2]-u[2]*v[1], u[2]*v[0]-u[0]*v[2], u[0]*v[1]-u[1]*v[0]];
          for (const i of f) for (let k = 0; k < 3; k++) n[i][k] += q[k];
        }
        return n.map((q) => { const z = Math.hypot(q[0], q[1], q[2]) || 1; return q.map((x) => x / z); });
      };
      const mk = (V, F, UV, E) => {
        const N = norms(V, F), m = {};
        const buf = (d) => {
          const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(d.flat ? d.flat() : d), gl.STATIC_DRAW);
          return b;
        };
        m.pb = buf(V); m.nb = buf(N); m.ub = buf(UV); m.eb = buf(E);
        m.ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.ib);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(F.flat()), gl.STATIC_DRAW);
        m.count = F.length * 3;
        return m;
      };
      this._meshes = {};
      for (const key of Object.keys(mesh.sizes)) {
        const s = mesh.sizes[key];
        this._meshes[key] = {
          front: mk(s.front, mesh.frontF, mesh.uv, s.edge),
          back: mk(s.back, mesh.backF, mesh.uv, s.edge)
        };
      }
      this._white = this._uploadTexture(null);
      if (this.settings.image) this.setImage(this.settings.image);
    }

    _uploadTexture(img) {
      const gl = this._gl;
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      if (img) {
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([251, 248, 244, 255]));
      }
      return t;
    }

    _swapTexture(t) {
      if (this._tex && this._gl) this._gl.deleteTexture(this._tex);  // no stale artwork
      this._tex = t;
      this.dispatchEvent(new CustomEvent('artworkchange', { detail: { image: this.settings.image } }));
    }

    get bleedMode() { return this.settings.bleedMode; }
    set bleedMode(v) { this.setBleed({ mode: v || 'auto' }); }
    get bleedColor() { return this.settings.bleedColor; }
    set bleedColor(v) { this.setBleed({ color: v || '#E2A88F' }); }
    get bleedWidth() { return this.settings.bleedWidth; }
    set bleedWidth(v) { this.setBleed({ width: +v || BLEED_DEFAULT }); }
    get reverseText() { return this.settings.reverseText; }
    set reverseText(v) { this.setReverse(v); }
    get reverseFont() { return this.settings.reverseFont; }
    set reverseFont(v) { this.setReverseFont(v); }
    get backPrint() { return this.settings.backPrint; }
    set backPrint(v) { this.setBackPrint(!!v && v !== '0' && v !== 'false'); }

    /* Pipeline stages, kept separate and ordered:
       removeBackground → cleanMask/cut → geometry → bleed composite.
       Nothing downstream runs until a real RGBA cutout exists. */
    async _process(img, token) {
      /* An upload that already carries a real alpha channel has been cut out already
         (Photoroom, remove.bg, a PNG export). Running removal again is worse than
         useless: the transparent areas composite to black first, so an enclosed gap
         like a donut's hole comes back as opaque black instead of staying open. */
      if (alreadyCutOut(img)) {
        this._cutout = img; this._provider = 'supplied';
        this._busy(null);
        this._afterCutout(img, token);
        return;
      }
      this._busy('Removing background…');
      let cutout = img, provider = 'none', real = false;
      try {
        const bg = await loadBG();
        const res = await bg.removeBackground(img, { maxSide: 1024 });
        cutout = res.canvas; provider = res.provider; real = !!res.real;
      } catch (e) {
        provider = 'none'; real = false;
      }
      if (token !== this._texToken || !this._gl) return;
      this._cutout = cutout; this._provider = provider;
      this._busy(null);

      /* A cutout the provider marks `real: false` is not trustworthy on a photographic
         background. Stop here and show the 2D result rather than building a pillow
         out of grass. Gate on the contract's `real` flag, never on a provider name,
         so any provider honouring removeBackground() (RMBG in dev, Photoroom in
         production) builds the pillow. */
      if (!real) {
        this._geom = null; this._disposeDynamic();
        this._flat(cutout);
        this._notice('BACKGROUND REMOVAL UNAVAILABLE HERE — SHOWING RAW CUTOUT, PILLOW NOT BUILT');
        if (DEBUG_VISUALISER) this._debugStrip(img, cutout);
        this.dispatchEvent(new CustomEvent('cutoutready', { bubbles: true, composed: true, detail: { provider, real: false } }));
        return;
      }

      this._afterCutout(cutout, token);
    }

    /* Geometry + composite from a trusted cutout, whoever produced it. */
    _afterCutout(cutout, token) {
      this._flat(null);
      this._notice(null);
      this._cutSilhouette(cutout, token);
      if (!this._geom) this._swapTexture(this._uploadTexture(cutout));
      this.dispatchEvent(new CustomEvent('cutoutready', {
        bubbles: true, composed: true, detail: { provider: this._provider, real: true }
      }));
    }

    /* 2D cutout on a transparency grid — the honest view when segmentation failed. */
    _flat(cutout) {
      if (!this._flatEl) {
        const el = document.createElement('div');
        el.style.cssText = 'position:absolute;inset:0;z-index:3;display:grid;place-items:center;padding:10px 10px 26px;background:conic-gradient(#E5DFD6 0 25%,#F4F0EA 0 50%,#E5DFD6 0 75%,#F4F0EA 0) 0 0/18px 18px';
        const im = document.createElement('img');
        im.alt = 'Background-removed cutout of your photo';
        im.style.cssText = 'max-width:100%;max-height:100%;display:block';
        el.appendChild(im);
        this.appendChild(el);
        this._flatEl = el; this._flatImg = im;
      }
      if (!cutout) { this._flatEl.style.display = 'none'; if (this._canvas) this._canvas.style.visibility = 'visible'; return; }
      this._flatImg.src = cutout.toDataURL ? cutout.toDataURL() : cutout.src;
      this._flatEl.style.display = 'grid';
      if (this._canvas) this._canvas.style.visibility = 'hidden';
    }

    _notice(text) {
      if (!this._noteEl) {
        const el = document.createElement('p');
        el.style.cssText = "position:absolute;left:0;right:0;bottom:0;margin:0;padding:6px 10px;z-index:5;font-family:'JetBrains Mono',monospace;font-size:8.5px;letter-spacing:.13em;color:#8C3F26;background:rgba(247,235,230,.92);text-align:center";
        this.appendChild(el);
        this._noteEl = el;
      }
      this._noteEl.textContent = text || '';
      this._noteEl.style.display = text ? 'block' : 'none';
    }

    _busy(text) {
      if (!this._busyEl) {
        const el = document.createElement('div');
        el.style.cssText = "position:absolute;inset:0;display:grid;place-items:center;z-index:4;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.15em;color:#6E645A;background:rgba(244,240,234,.72);backdrop-filter:blur(2px);text-align:center;padding:16px";
        this.appendChild(el);
        this._busyEl = el;
      }
      this._busyEl.textContent = text || '';
      this._busyEl.style.display = text ? 'grid' : 'none';
    }

    /* Developer-only: original / raw removal / cleaned cutout on a chequerboard,
       so surviving background pixels are obvious. */
    _debugStrip(original, cutout) {
      const host = this._dbg || (() => {
        const d = document.createElement('div');
        d.style.cssText = 'position:absolute;left:8px;bottom:8px;z-index:6;display:flex;gap:6px;padding:6px;border-radius:10px;background:rgba(251,248,244,.94)';
        this.appendChild(d); this._dbg = d; return d;
      })();
      host.textContent = '';
      const chequer = 'conic-gradient(#ccc 0 25%,#fff 0 50%,#ccc 0 75%,#fff 0) 0 0/14px 14px';
      const cell = (label, src) => {
        const box = document.createElement('div');
        box.style.cssText = "display:flex;flex-direction:column;gap:3px;font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:.1em;color:#6E645A";
        const frame = document.createElement('div');
        frame.style.cssText = 'width:96px;height:96px;background:' + chequer + ';display:grid;place-items:center;overflow:hidden;border-radius:6px';
        const im = document.createElement('img');
        im.src = src; im.style.cssText = 'max-width:100%;max-height:100%;display:block';
        frame.appendChild(im); box.appendChild(frame);
        const t = document.createElement('span'); t.textContent = label; box.appendChild(t);
        return box;
      };
      const toURL = (c) => (c.toDataURL ? c.toDataURL() : c.src);
      host.appendChild(cell('A ORIGINAL', original.src || toURL(original)));
      host.appendChild(cell('B RAW REMOVAL (' + (this._provider || '?') + ')', toURL(cutout)));
      if (this._geom) host.appendChild(cell('C CLEANED', this._cleanPreview()));
    }

    _cleanPreview() {
      const g = this._geom;
      const cv = document.createElement('canvas');
      cv.width = g.w; cv.height = g.h;
      const ctx = cv.getContext('2d');
      ctx.drawImage(this._cutout, 0, 0, g.w, g.h);
      const id = ctx.getImageData(0, 0, g.w, g.h);
      for (let i = 0; i < g.w * g.h; i++) {
        if (g.sdfSubject[i] < 0) id.data[i * 4 + 3] = 0;
      }
      ctx.putImageData(id, 0, 0);
      return cv.toDataURL();
    }

    _bleedPx() {
      const g = this._geom;
      if (!g) return 0;
      if (this.settings.bleedMode === 'none') return 0;
      /* width is mm against the NOMINAL canvas, so a 4000px and a 1000px photo
         receive the same visual border */
      return (this.settings.bleedWidth / NOMINAL) * Math.max(g.w, g.h);
    }

    _bleedRgb() {
      /* "No border" still needs fabric colour where closing bridged a gap between
         legs — the auto match reads as material there, not as a deliberate border. */
      if (this.settings.bleedMode !== 'color') return this._geom ? this._geom.auto : [226, 168, 143];
      return hexToRgb(this.settings.bleedColor);
    }

    /* ---- dynamic silhouette ----------------------------------------
       subjectMask drives the artwork, outerPillowMask drives the geometry.
       They are kept separate: the outer mask is an offset contour of the same
       distance field, never a replacement for the subject. */
    _cutSilhouette(img, token) {
      const gl = this._gl;
      if (!gl) return;
      let m = null;
      try { m = maskFromImage(img); } catch (e) { m = null; }
      if (token !== this._texToken || !this._gl) return;
      if (!m) { this._geom = null; this._disposeDynamic(); return; }

      const { mask, solid, w, h, data } = m;
      /* Trim hairline filaments — a wisp of branch or fence the matte kept attached
         to the subject, which then prints as a stray mark out on the border. Radius
         is deliberately small: it must kill 2–4px wisps without eating thin legs. */
      const openR = Math.max(2, Math.round(Math.max(w, h) * 0.008));
      let subject = morphOpen(mask, w, h, openR);
      /* fall back only if opening was catastrophic (a very thin subject overall) */
      let kept = 0; for (let i = 0; i < w * h; i++) kept += subject[i];
      let orig = 0; for (let i = 0; i < w * h; i++) orig += mask[i];
      if (kept < orig * 0.80) subject = mask;
      const closeR = CLOSE_FRAC * Math.max(w, h);
      /* The pillow outline gets the same filament trim as the artwork, so a wisp or
         thin-necked blob the matte left attached does not become a protrusion once
         closing rounds it off. */
      let base = morphOpen(solid, w, h, openR);
      let bk = 0, bo = 0;
      for (let i = 0; i < w * h; i++) { bk += base[i]; bo += solid[i]; }
      if (bk < bo * 0.80) base = solid;
      const closed = morphClose(base, w, h, closeR);       // manufacturable, not die-cut

      /* Pad the field grid. A photo cropped through its subject leaves the mask
         touching the canvas, and the contour then has nowhere to close: aEdge never
         reaches 0, so the panels stay held apart with no rim joining them. Padding
         gives the seam room to close naturally, as a rounded rim rather than the
         straight chop a boundary clamp produces. */
      const P = Math.ceil(Math.max(w, h) * 0.09);
      const W2 = w + 2 * P, H2 = h + 2 * P;
      const padded = (src) => {
        const o = new Uint8Array(W2 * H2);
        for (let y = 0; y < h; y++) o.set(src.subarray(y * w, y * w + w), (y + P) * W2 + P);
        return o;
      };
      const sdfSubject = signedField(padded(subject), W2, H2);   // artwork alpha
      const smoothR = Math.max(1, Math.round(Math.max(W2, H2) * 0.026));
      const sdfOuter = boxBlurField(signedField(padded(closed), W2, H2), W2, H2, smoothR, 3);

      /* auto-match samples a ring just inside the subject edge, not the whole photo */
      const ring = [];
      for (let y = 0; y < H2; y++) for (let x = 0; x < W2; x++) {
        const s = sdfSubject[y * W2 + x];
        if (s <= 1 || s >= 5) continue;
        const ox = x - P, oy = y - P;
        if (ox < 0 || oy < 0 || ox >= w || oy >= h) continue;
        const i = oy * w + ox;
        ring.push([data[i * 4], data[i * 4 + 1], data[i * 4 + 2]]);
      }
      const median = (k) => {
        if (!ring.length) return 226;
        const v = ring.map((c) => c[k]).sort((a, b) => a - b);
        return v[v.length >> 1];
      };
      const auto = ring.length ? [median(0), median(1), median(2)] : [226, 168, 143];
      /* Contour resolution. Coarse cells make a round subject read as a polygon,
         so the grid is sampled finely; vertex count stays well inside the 16-bit
         index limit because only boundary cells emit geometry. */
      const step = Math.max(2, Math.round(Math.min(W2, H2) / 150));
      const suggest = [];
      const far = (hex) => suggest.every((s) => {
        const a = hexToRgb(s), b = hexToRgb(hex);
        return Math.hypot(a[0]-b[0], a[1]-b[1], a[2]-b[2]) > 34;
      });
      for (let i = 0; i < ring.length && suggest.length < 5; i += Math.max(1, Math.floor(ring.length / 40))) {
        const hex = rgbToHex(ring[i]);
        if (far(hex)) suggest.push(hex);
      }
      if (suggest.length < 3) {
        ['#F4EFE9', '#E2A88F', '#CBB9A6'].forEach((h) => { if (suggest.length < 5 && far(h)) suggest.push(h); });
      }

      this._geom = { w: W2, h: H2, pad: P, iw: w, ih: h, sdfSubject, sdfOuter, auto, step };
      this._img = this._cutout || img;      // composite draws the CUTOUT, never the raw photo
      this._suggest = suggest;
      this._rebuildFromGeom();
      this.dispatchEvent(new CustomEvent('bleedpalette', {
        bubbles: true, composed: true,
        detail: { auto: rgbToHex(auto), suggestions: suggest }
      }));
    }

    _rebuildFromGeom() {
      const g = this._geom, gl = this._gl;
      if (!g || !gl) return;
      const { w, h, sdfOuter, step } = g;
      const level = -this._bleedPx();          // outer contour = offset path
      /* A photo cropped through its subject leaves the mask running off the canvas.
         Without a boundary there, the contour never closes: aEdge stays large, so the
         front and back panels are held apart at full depth with no rim joining them —
         an open gap you can see straight through. Forcing the field negative in a
         frame guarantees a closing seam inside the image, which is also what a real
         pillow has: every edge is sewn. */
      const margin = 1;
      const frame = (x, y) => Math.min(x, y, (w - 1) - x, (h - 1) - y) - margin;

      /* Round subjects: fair the outline radially.
         A donut photographed slightly wider than tall leaves a broad bulge at its
         widest point, and the outward bleed offset preserves it as a lump. Blurring the
         field cannot remove it — blur acts on curvature, and a broad bulge has little.
         So for shapes that are star-shaped about their centre (circles, balls, plates,
         faces) the radius-versus-angle profile is low-pass filtered, which removes a
         radial bulge while keeping the silhouette. Shapes that fail the star test — a
         dog with legs, anything with concavities — are left exactly as they were. */
      let field = sdfOuter, lvl = level;
      (() => {
        const N = 180, TAU2 = Math.PI * 2;
        let sx = 0, sy = 0, n = 0;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (sdfOuter[y * w + x] - level >= 0) { sx += x; sy += y; n++; }
        }
        if (n < 400) return;
        const cx0 = sx / n, cy0 = sy / n;
        const rad = new Float32Array(N);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          if (sdfOuter[y * w + x] - level < 0) continue;
          const dx = x - cx0, dy = y - cy0;
          const b = Math.min(N - 1, Math.max(0, Math.floor(((Math.atan2(dy, dx) + Math.PI) / TAU2) * N)));
          const r = Math.hypot(dx, dy);
          if (r > rad[b]) rad[b] = r;
        }
        /* Only near-circular outlines are faired. Gap-closing already turns a dog into
           a smooth blob, so "star-shaped" is true of almost everything and cannot be the
           test. Roundness can: a round pillow's radius barely varies with angle, while a
           standing dog's blob is strongly elongated. Anything outside the band is left
           exactly as it was. */
        const sorted = Array.from(rad).sort((a, b) => a - b);
        const med = sorted[N >> 1];
        if (!(med > 0)) return;
        let mn = Infinity, mx = 0;
        for (let i = 0; i < N; i++) { if (rad[i] < mn) mn = rad[i]; if (rad[i] > mx) mx = rad[i]; }
        if (mx / med > 1.22 || mn / med < 0.78) return;      // not round enough
        const k = Math.max(2, Math.round(N * 0.07));
        let cur = rad;
        for (let pass = 0; pass < 2; pass++) {
          const out = new Float32Array(N);
          for (let i = 0; i < N; i++) {
            let acc = 0;
            for (let j = -k; j <= k; j++) acc += cur[(i + j + N) % N];
            out[i] = acc / (2 * k + 1);
          }
          cur = out;
        }
        const faired = new Float32Array(w * h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          const dx = x - cx0, dy = y - cy0;
          const a = (Math.atan2(dy, dx) + Math.PI) / TAU2 * N;
          const i0 = Math.floor(a) % N, i1 = (i0 + 1) % N, t = a - Math.floor(a);
          faired[y * w + x] = (cur[i0] + (cur[i1] - cur[i0]) * t) - Math.hypot(dx, dy);
        }
        field = faired; lvl = 0;
      })();
      this._faired = { field, lvl };
      const cols = Math.floor((w - 1) / step), rows = Math.floor((h - 1) / step);
      if (cols < 3 || rows < 3) { this._disposeDynamic(); return; }
      const at = (i, j) => {
        const x = Math.min(w - 1, i * step), y = Math.min(h - 1, j * step);
        return Math.min(field[y * w + x] - lvl, frame(x, y));
      };
      const scale = NOMINAL / Math.max(w, h);
      const cx = (w - 1) / 2, cy = (h - 1) / 2;

      const V = [], UV = [], E = [], F = [], seen = new Map();
      let perimeter = 0;
      const vert = (px, py, edgePx) => {
        const key = (px * 4 | 0) + ':' + (py * 4 | 0);
        let id = seen.get(key);
        if (id !== undefined) {
          /* A contour crossing can land on a grid corner and dedup onto it. The rim
             value must win: if the merged vertex keeps the corner's aEdge, the shader
             holds the two panels apart at that one point and you get a pinhole at the
             seam — visible as the back panel's print showing through as it rotates. */
          if (edgePx === 0 && E[id] > 0) E[id] = 0;
          return id;
        }
        id = V.length;
        V.push([(px - cx) * scale, (cy - py) * scale, 1]);
        UV.push([Math.min(1, Math.max(0, px / (w - 1))), Math.min(1, Math.max(0, py / (h - 1)))]);
        E.push(Math.max(0, edgePx) * scale);   // distance from the sewn OUTER rim
        seen.set(key, id);
        return id;
      };

      const gx = (ci) => Math.min(w - 1, ci * step);
      const gy = (cj) => Math.min(h - 1, cj * step);
      /* Marching TRIANGLES rather than squares. A square cell with two diagonally
         opposite corners inside is ambiguous (the saddle case): the 6-point polygon
         it yields is not convex, so fan triangulation folds it into a bowtie and
         leaves an open gap in the shell — the hole seen when rotating. A triangle
         has no ambiguous case, so clipping two triangles per cell is closed by
         construction. Vertices are shared through the same dedup map, so adjacent
         triangles cannot part company. */
      const clip = (tri) => {
        const poly = [], cross = [];
        for (let k = 0; k < 3; k++) {
          const a = tri[k], b = tri[(k + 1) % 3];
          if (a[2] >= 0) poly.push(vert(a[0], a[1], a[2]));
          if ((a[2] >= 0) !== (b[2] >= 0)) {
            const t = a[2] / (a[2] - b[2]);
            const cxp = a[0] + (b[0] - a[0]) * t, cyp = a[1] + (b[1] - a[1]) * t;
            cross.push([cxp, cyp]);
            poly.push(vert(cxp, cyp, 0));
          }
        }
        if (cross.length === 2) perimeter += Math.hypot(cross[0][0] - cross[1][0], cross[0][1] - cross[1][1]);
        for (let k = 1; k + 1 < poly.length; k++) {
          if (poly[0] !== poly[k] && poly[k] !== poly[k + 1] && poly[0] !== poly[k + 1]) {
            F.push([poly[0], poly[k], poly[k + 1]]);
          }
        }
      };
      for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
        const c = [
          [gx(i), gy(j), at(i, j)],
          [gx(i + 1), gy(j), at(i + 1, j)],
          [gx(i + 1), gy(j + 1), at(i + 1, j + 1)],
          [gx(i), gy(j + 1), at(i, j + 1)]
        ];
        if (c[0][2] < 0 && c[1][2] < 0 && c[2][2] < 0 && c[3][2] < 0) continue;
        clip([c[0], c[1], c[2]]);
        clip([c[0], c[2], c[3]]);
      }
      if (V.length < 16 || !F.length || V.length > 60000) { this._disposeDynamic(); return; }

      const normalsFor = (side) => {
        const P = V.map((p, i) => {
          const yn = Math.min(Math.max(p[1] / NOMINAL + 0.5, 0), 1);
          return [p[0], p[1], side * profileZ(E[i], yn)];
        });
        const N = P.map(() => [0, 0, 0]);
        for (const f of F) {
          const a = P[f[0]], b = P[f[1]], c = P[f[2]];
          const u = [b[0]-a[0], b[1]-a[1], b[2]-a[2]], v = [c[0]-a[0], c[1]-a[1], c[2]-a[2]];
          const q = [u[1]*v[2]-u[2]*v[1], u[2]*v[0]-u[0]*v[2], u[0]*v[1]-u[1]*v[0]];
          for (const idx of f) for (let k = 0; k < 3; k++) N[idx][k] += q[k];
        }
        return N.map((q) => {
          const len = Math.hypot(q[0], q[1], q[2]) || 1;
          const u = [q[0]/len, q[1]/len, q[2]/len];
          /* Mirror only the z component when it faces inward. Flipping the whole
             vector also reverses x and y, which inverts the lighting in small
             patches and reads as blemishes on the border. */
          return (u[2] * side < 0) ? [u[0], u[1], -u[2]] : u;
        });
      };

      const front = this._mkMesh(V.map((p) => [p[0], p[1], 1]), F, UV, normalsFor(1), E);
      /* Back panel keeps the front's UVs. Seen from behind the outline is mirrored too,
         so mirroring the UVs un-mirrors the artwork but stops it matching its own
         shape. Artwork therefore reads mirrored on the back (as it physically would);
         only our own overlaid message is compensated, in _buildReverse. */
      const back = this._mkMesh(V.map((p) => [p[0], p[1], -1]), F, UV, normalsFor(-1), E);
      /* Bounds let the camera frame any silhouette to the same preview area
         instead of every shape sharing one fixed scale. */
      let hw = 1, hh = 1;
      for (const p of V) { hw = Math.max(hw, Math.abs(p[0])); hh = Math.max(hh, Math.abs(p[1])); }
      this._bounds = { hw, hh };
      /* Whole number of stitches around the perimeter, ~7mm apart. */
      this._stitchN = Math.max(18, Math.round((perimeter * scale) / 15));
      this._disposeDynamic();
      this._dynamic = { front, back };
      this._recomposite();
    }

    /* Bleed is printed fabric: it is composited into the texture that wraps the
       geometry, so it turns and deforms with the pillow. Customer pixels are drawn
       on top, unscaled and unmodified. */
    _recomposite() {
      const g = this._geom, img = this._img, gl = this._gl;
      if (!g || !img || !gl) return;
      const { w, h, pad: P, iw, ih, sdfSubject, sdfOuter } = g;
      /* the texture spans the padded grid, so border fabric beyond the photo edge
         has texels of its own; the photo itself is drawn at the pad offset */
      const TW = Math.min(1200, Math.max(256, Math.round(img.width * (w / iw))));
      const TH = Math.max(1, Math.round(TW * (h / w)));
      const cv = document.createElement('canvas'); cv.width = TW; cv.height = TH;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, (P / w) * TW, (P / h) * TH, (iw / w) * TW, (ih / h) * TH);
      let id;
      try { id = ctx.getImageData(0, 0, TW, TH); } catch (e) { return; }
      const d = id.data;
      const level = -this._bleedPx();
      const col = this._bleedRgb();
      const sx = (w - 1) / (TW - 1 || 1), sy = (h - 1) / (TH - 1 || 1);
      const soft = (v, k) => Math.max(0, Math.min(1, v / k + 0.5));
      for (let y = 0; y < TH; y++) for (let x = 0; x < TW; x++) {
        const o = (y * TW + x) * 4;
        const mx = x * sx, my = y * sy;
        /* same field the geometry was cut from, so texture and mesh agree */
        const fld = this._faired || { field: sdfOuter, lvl: level };
        const outer = Math.min(bilinear(fld.field, w, h, mx, my) - fld.lvl,
                               Math.min(mx, my, (w - 1) - mx, (h - 1) - my) - 1);
        /* Keep the texture fully opaque a little beyond the geometry rim, so rim
           texels never sample the transparent edge and flash white. */
        if (outer <= -3.5) { d[o + 3] = 0; continue; }
        const aOuter = soft(outer + 2.2, 1.4);
        /* Trim ~2px inward so the matte's fringe of original background never
           prints; the border colour takes that ring instead. */
        const aSubj = soft(bilinear(sdfSubject, w, h, mx, my) - 2.0, 1.2);
        d[o]     = d[o]     * aSubj + col[0] * (1 - aSubj);
        d[o + 1] = d[o + 1] * aSubj + col[1] * (1 - aSubj);
        d[o + 2] = d[o + 2] * aSubj + col[2] * (1 - aSubj);
        d[o + 3] = 255 * aOuter;
      }
      ctx.putImageData(id, 0, 0);
      this._lastComposite = cv;
      this._swapTexture(this._uploadTexture(cv));
      this._buildReverse(TW, TH, d, col);
    }

    /* Reverse panel. The dog print on the back is optional and independent of the
       message, so a customer can have either, both or neither. When the back carries
       the print and no message, the front texture is reused as-is. Alpha is copied
       from the front composite so both panels share one silhouette exactly. */
    _buildReverse(TW, TH, frontData, col) {
      if (!this._gl) { this._swapBackTexture(null); return; }
      const text = (this.settings.reverseText || '').trim();
      const printOnBack = !!this.settings.backPrint;
      if (printOnBack && !text) { this._swapBackTexture(null); return; }
      const cv = document.createElement('canvas'); cv.width = TW; cv.height = TH;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      if (!ctx) { this._swapBackTexture(null); return; }
      if (printOnBack) {
        ctx.putImageData(new ImageData(new Uint8ClampedArray(frontData), TW, TH), 0, 0);
      } else {
        ctx.fillStyle = 'rgb(' + col[0] + ',' + col[1] + ',' + col[2] + ')';
        ctx.fillRect(0, 0, TW, TH);
      }

      /* ink that reads against the chosen fabric */
      if (text) {
      const lum = (0.299 * col[0] + 0.587 * col[1] + 0.114 * col[2]) / 255;
      /* Light ink over artwork, luminance-matched ink on plain fabric. Either way the
         text carries a heavy contrasting shadow so it stays readable over any print. */
      const lightInk = printOnBack || lum <= 0.55;
      ctx.fillStyle = lightInk ? '#FDFAF6' : '#231D1A';
      ctx.shadowColor = lightInk ? 'rgba(0,0,0,.66)' : 'rgba(255,255,255,.8)';
      ctx.shadowBlur = Math.max(5, TW * 0.03);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.save();
      ctx.translate(TW, 0); ctx.scale(-1, 1);   // so the message reads from behind

      /* Keep the message inside the printed subject area — the region the animal
         occupies — so it never runs out onto the bleed border. The band's narrowest
         horizontal run is used, so text cannot spill into a concave pocket either. */
      const g = this._geom;
      const sx = (g.w - 1) / (TW - 1 || 1), sy = (g.h - 1) / (TH - 1 || 1);
      const inSubject = (tx, ty) => bilinear(g.sdfSubject, g.w, g.h, tx * sx, ty * sy) > 0;
      let bx0 = TW, bx1 = 0, by0 = TH, by1 = 0;
      for (let ty = 0; ty < TH; ty += 2) for (let tx = 0; tx < TW; tx += 2) {
        if (!inSubject(tx, ty)) continue;
        if (tx < bx0) bx0 = tx; if (tx > bx1) bx1 = tx;
        if (ty < by0) by0 = ty; if (ty > by1) by1 = ty;
      }
      if (bx1 - bx0 < 8 || by1 - by0 < 8) { bx0 = TW * 0.2; bx1 = TW * 0.8; by0 = TH * 0.2; by1 = TH * 0.8; }
      const bcx = (bx0 + bx1) / 2, bcy = (by0 + by1) / 2;
      const runAt = (ty) => {
        let first = -1, last = -1;
        for (let tx = Math.floor(bx0); tx <= bx1; tx += 2) {
          if (inSubject(tx, Math.round(ty))) { if (first < 0) first = tx; last = tx; }
        }
        return first < 0 ? 0 : last - first;
      };
      const bandWidth = (halfH) => {
        let min = Infinity;
        const stepY = Math.max(2, halfH / 4);
        for (let ty = bcy - halfH; ty <= bcy + halfH; ty += stepY) min = Math.min(min, runAt(ty));
        return min === Infinity ? 0 : min;
      };

      const wrap = (px, maxW) => {
        ctx.font = '400 ' + px + 'px ' + (this.settings.reverseFont || REVERSE_FONT_DEFAULT);
        const out = [];
        /* Explicit customer line breaks are hard boundaries. Each typed line is then
           word-wrapped on its own, exactly as before, if it is too wide for the band. */
        for (const para of String(text).split(/\r\n|\r|\n/)) {
          const words = para.split(/[^\S\r\n]+/).filter(Boolean);
          if (!words.length) { out.push(''); continue; }
          let line = '';
          for (const word of words) {
            const test = line ? line + ' ' + word : word;
            if (ctx.measureText(test).width > maxW && line) { out.push(line); line = word; }
            else line = test;
          }
          if (line) out.push(line);
        }
        return out;
      };

      let size = Math.max(10, Math.round((bx1 - bx0) * 0.14));
      let lines = [];
      for (let guard = 0; guard < 26; guard++) {
        const lh0 = size * 1.22;
        let maxW = bandWidth(lh0) * 0.72;
        if (maxW < size) maxW = (bx1 - bx0) * 0.58;
        lines = wrap(size, maxW);
        const halfH = Math.max(lh0, (lines.length * lh0) / 2);
        const fitW = bandWidth(halfH) * 0.72;
        ctx.font = '400 ' + size + 'px ' + (this.settings.reverseFont || REVERSE_FONT_DEFAULT);
        const widest = lines.reduce((m, l) => Math.max(m, ctx.measureText(l).width), 0);
        const fitsH = lines.length * lh0 <= (by1 - by0) * 0.66;
        if (widest <= Math.max(fitW, 1) && fitsH && lines.length <= 5) break;
        size = Math.round(size * 0.9);
        if (size < 10) { size = 10; lines = wrap(size, Math.max(bandWidth(size) * 0.72, (bx1 - bx0) * 0.58)); break; }
      }
      ctx.font = '400 ' + size + 'px ' + (this.settings.reverseFont || REVERSE_FONT_DEFAULT);
      const lh = size * 1.22;
      const y0 = bcy - ((lines.length - 1) * lh) / 2;
      /* three passes build the shadow up to a real drop shadow rather than a haze */
      lines.forEach((l, i) => {
        for (let p = 0; p < 3; p++) ctx.fillText(l, TW - bcx, y0 + i * lh);
      });
      ctx.restore();
      }

      const bd = ctx.getImageData(0, 0, TW, TH);
      for (let i = 0; i < TW * TH; i++) bd.data[i * 4 + 3] = frontData[i * 4 + 3];
      ctx.putImageData(bd, 0, 0);
      this._swapBackTexture(this._uploadTexture(cv));
    }

    _swapBackTexture(t) {
      if (this._texBack && this._gl) this._gl.deleteTexture(this._texBack);
      this._texBack = t;
    }

    _mkMesh(V, F, UV, N, E) {
      const gl = this._gl, m = {};
      const buf = (data) => {
        const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data.flat ? data.flat() : data), gl.STATIC_DRAW);
        return b;
      };
      m.pb = buf(V); m.nb = buf(N); m.ub = buf(UV); m.eb = buf(E);
      m.ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.ib);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(F.flat()), gl.STATIC_DRAW);
      m.count = F.length * 3;
      return m;
    }

    _disposeDynamic() {
      const gl = this._gl;
      if (!this._dynamic || !gl) { this._dynamic = null; return; }
      for (const side of ['front', 'back']) {
        const mm = this._dynamic[side];
        ['pb', 'nb', 'ub', 'eb'].forEach((b) => gl.deleteBuffer(mm[b]));
        gl.deleteBuffer(mm.ib);
      }
      this._dynamic = null;
    }

    _releaseGL() {
      const gl = this._gl;
      if (!gl) return;
      if (this._tex) gl.deleteTexture(this._tex);
      if (this._texBack) gl.deleteTexture(this._texBack);
      this._disposeDynamic();
      if (this._white) gl.deleteTexture(this._white);
      for (const k of Object.keys(this._meshes)) {
        for (const side of ['front', 'back']) {
          const m = this._meshes[k][side];
          ['pb', 'nb', 'ub', 'eb'].forEach((b) => gl.deleteBuffer(m[b]));
          gl.deleteBuffer(m.ib);
        }
      }
      if (this._prog) gl.deleteProgram(this._prog);
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      this._gl = null; this._tex = null; this._white = null; this._meshes = {};
    }

    _start() {
      if (this._running || !this._gl) return;
      this._running = true;
      const loop = (now) => {
        if (!this._running) return;
        const t = now || performance.now();
        this._autorotate(t);
        this._applyTween(t);
        this._frame();
        this._raf = requestAnimationFrame(loop);
      };
      this._raf = requestAnimationFrame(loop);
    }
    _stop() { this._running = false; if (this._raf) cancelAnimationFrame(this._raf); }

    _frame() {
      const gl = this._gl, canvas = this._canvas, L = this._L;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      const w = Math.max(2, Math.floor(r.width * dpr)), h = Math.max(2, Math.floor(r.height * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }

      gl.enable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      const set = this.settings;
      const step = PLUMP_STEPS[set.plump] || PLUMP_STEPS[1];
      const v = this._view;
      const FOV = 34 * Math.PI / 180;
      let fit;
      if (this._dynamic && this._bounds) {
        /* Fill the preview area, leaving margin for the depth swing while rotating. */
        const halfY = Math.tan(FOV / 2) * v.dist;
        const halfX = halfY * (w / h);
        const reach = Math.hypot(this._bounds.hw, step.depth * 0.5);
        /* 0.86 rather than 0.95: pitch, the seam bulge and the ground shadow all add a
           little to the projected height, and clipping the top of a tall subject is far
           worse than a small margin. */
        fit = Math.min(halfX * 0.82 / reach, halfY * 0.82 / this._bounds.hh);
      } else {
        fit = 1.45 * (300 / set.size);
      }
      const model = mul(ry(v.yaw), mul(rx(v.pitch), scM(fit)));
      /* Nudge the object down so it sits clear of the stage's top edge. */
      const view = trM(-v.dist);
      view[13] = -0.02 * (this._bounds ? this._bounds.hh : NOMINAL / 2) * fit;
      const mvp = mul(persp(FOV, w / h, 1, 3000), mul(view, model));
      const pair = this._dynamic || this._meshes[set.size] || this._meshes[300];
      /* How many device pixels one woven thread covers right now. Below about two the
         weave cannot be resolved and must fade, or it turns into a moire pattern. */
      const pxPerMM = h * fit / (2 * Math.tan(FOV / 2) * v.dist);
      const cellPx = (NOMINAL / WEAVE_FREQ) * pxPerMM;
      const weaveVis = Math.max(0, Math.min(1, (cellPx - 1.8) / 3.2));
      const tex = this._tex || this._white;
      /* The reverse panel gets its own texture when the customer adds a message, so
         the back of the pillow prints the message instead of the photo. */
      const texBack = this._texBack || tex;

      const draw = (m, useTex) => {
        for (const [b, a, s] of [[m.pb, L.pos, 3], [m.nb, L.n, 3], [m.ub, L.uv, 2], [m.eb, L.edge, 1]]) {
          gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.enableVertexAttribArray(a);
          gl.vertexAttribPointer(a, s, gl.FLOAT, false, 0, 0);
        }
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.ib);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, useTex || tex);
        gl.uniformMatrix4fv(L.mvp, false, new Float32Array(mvp));
        gl.uniformMatrix4fv(L.model, false, new Float32Array(model));
        gl.uniform1f(L.use, 1); gl.uniform1f(L.vel, set.velvet);
        gl.uniform3fv(L.base, new Float32Array([1, 1, 1]));
        gl.uniform1f(L.puff, step.puff);
        gl.uniform1f(L.tubby, step.tubby);
        gl.uniform1f(L.depth, step.depth);
        gl.uniform1f(L.height, this._dynamic ? NOMINAL : set.size);
        gl.uniform1f(L.stitchN, this._stitchN || 60);
        gl.uniform1f(L.weave, weaveVis);
        gl.drawElements(gl.TRIANGLES, m.count, gl.UNSIGNED_SHORT, 0);
      };
      /* Inner core: the same shells at 88% depth, drawn first. Any pinhole left by
         tessellation shows fabric behind it instead of the background. */
      if (!this._soloFront) {
        gl.uniform1f(L.zscale, 0.88);
        draw(pair.front, tex); draw(pair.back, texBack);
      }
      gl.uniform1f(L.zscale, 1.0);
      draw(pair.front, tex);
      if (!this._soloFront) draw(pair.back, texBack);
    }

    _fallback(err) {
      this._stop();
      const note = document.createElement('p');
      note.style.cssText = "margin:0;padding:18px;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.13em;color:#8A7E71;text-align:center";
      note.textContent = 'PREVIEW UNAVAILABLE ON THIS DEVICE';
      this.appendChild(note);
      if (DEBUG_VISUALISER) console.warn('[plumpy-visualiser]', err);
    }

    /* Developer-only tuning surface. Never present for customers. */
    _debugPanel() {
      const box = document.createElement('div');
      box.style.cssText = 'position:absolute;left:8px;top:8px;z-index:5;display:flex;flex-direction:column;gap:6px;padding:10px;border-radius:10px;background:rgba(251,248,244,.94);font:11px/1.4 monospace';
      [['plump', 0, 2, 1], ['size', 200, 400, 100]].forEach(([key, min, max, stepv]) => {
        const row = document.createElement('label');
        row.textContent = key;
        const input = document.createElement('input');
        input.type = 'range'; input.min = min; input.max = max; input.step = stepv;
        input.value = this.settings[key];
        input.oninput = () => { key === 'plump' ? this.setPlumpness(+input.value) : this.setSize(+input.value); };
        row.appendChild(input);
        box.appendChild(row);
      });
      this.appendChild(box);
    }
  }

  if (!customElements.get('plumpy-visualiser')) customElements.define('plumpy-visualiser', PlumpyVisualiser);
})();